import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';

import { 
  XMarkIcon, 
  PaperClipIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  UserIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useKeycloak } from '@react-keycloak/web';

import { useDMSRightPanelStore } from '../store/dmsRightPanelStore';
import { useDocumentAPI } from '../DocumentAPI';
import { useDocumentCategoryAPI, DocumentCategory } from '../DocumentCategoryAPI';
import { useDocumentTemplateAPI, DocumentTemplate } from '../DocumentTemplateAPI';
import { UserPageSetupDefaultsService } from '../EdgeDocs/ClientFirst/services/UserPageSetupDefaultsService';
import { getSystemPreferences, SystemPreferences } from '../../admin/services/SystemPreferencesAPI';
import { DocumentLogger } from '../../../utils/loggers/DocumentLogger';


/**
 * Client/Matter Selection Component
 */
interface ClientMatterSelectorProps {
  selectedClientId: string | null;
  selectedMatterId: string | null;
  onClientChange: (clientId: string | null) => void;
  onMatterChange: (matterId: string | null) => void;
}

const ClientMatterSelector: React.FC<ClientMatterSelectorProps> = ({
  selectedClientId,
  selectedMatterId,
  onClientChange,
  onMatterChange
}) => {
  const { keycloak } = useKeycloak();
  const [clients, setClients] = useState<any[]>([]);
  const [matters, setMatters] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingMatters, setLoadingMatters] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [mattersError, setMattersError] = useState<string | null>(null);

  // Real API integration for clients
  useEffect(() => {
    const loadClients = async () => {
      if (!keycloak?.token) return;
      
      setLoadingClients(true);
      setClientsError(null);
      try {
        const response = await fetch('/api/v1/clients', {
          headers: {
            'Authorization': `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load clients: ${response.statusText}`);
        }
        
        const data = await response.json();
        setClients(data.clients || data.data || data || []);
      } catch (error) {
        console.error('Error loading clients:', error);
        setClientsError(error instanceof Error ? error.message : 'Failed to load clients');
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    loadClients();
  }, [keycloak?.token]);

  // Real API integration for matters
  useEffect(() => {
    const loadMatters = async () => {
      if (!selectedClientId || !keycloak?.token) {
        setMatters([]);
        return;
      }

      setLoadingMatters(true);
      setMattersError(null);
      try {
        const response = await fetch(`/api/v1/matters?client_id=${selectedClientId}`, {
          headers: {
            'Authorization': `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load matters: ${response.statusText}`);
        }
        
        const data = await response.json();
        setMatters(data.matters || data.data || data || []);
      } catch (error) {
        console.error('Error loading matters:', error);
        setMattersError(error instanceof Error ? error.message : 'Failed to load matters');
        setMatters([]);
      } finally {
        setLoadingMatters(false);
      }
    };

    loadMatters();
  }, [selectedClientId, keycloak?.token]);

  return (
    <div className="space-y-4">
      {/* Client Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <UserIcon className="w-4 h-4 inline mr-2" />
          Select Client
        </label>
        <select
          value={selectedClientId || ''}
          onChange={(e) => onClientChange(e.target.value || null)}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
          disabled={loadingClients}
        >
          <option value="">
            {loadingClients ? 'Loading clients...' : 'Select a client...'}
          </option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>
              {client.name} {client.type && `(${client.type})`}
            </option>
          ))}
        </select>
        {clientsError && (
          <div className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
            <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
            {clientsError}
          </div>
        )}
      </div>

      {/* Matter Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <BriefcaseIcon className="w-4 h-4 inline mr-2" />
          Select Matter
        </label>
        <select
          value={selectedMatterId || ''}
          onChange={(e) => onMatterChange(e.target.value || null)}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
          disabled={loadingMatters || !selectedClientId}
        >
          <option value="">
            {loadingMatters ? 'Loading matters...' : 
             !selectedClientId ? 'Select a client first' : 
             'Select a matter...'}
          </option>
          {matters.map(matter => (
            <option key={matter.id} value={matter.id}>
              {matter.title} {matter.status && `(${matter.status})`}
            </option>
          ))}
        </select>
        {mattersError && (
          <div className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
            <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
            {mattersError}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Document Classification Component
 */
interface DocumentClassificationProps {
  documentType: string | null;
  onDocumentTypeChange: (type: string | null) => void;
  selectedTemplate: string | null;
  onTemplateChange: (templateId: string | null) => void;
}

const DocumentClassification: React.FC<DocumentClassificationProps> = ({
  documentType,
  onDocumentTypeChange,
  selectedTemplate,
  onTemplateChange
}) => {
  // Category API hook and state
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const categoryAPI = useDocumentCategoryAPI();

  // Template API hook and state
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const templateAPI = useDocumentTemplateAPI();

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      setCategoriesError(null);
      try {
        const fetchedCategories = await categoryAPI.getDocumentCategories();
        setCategories(fetchedCategories);
      } catch (error) {
        setCategoriesError('Failed to load categories');
        console.error('Error loading document categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, [categoryAPI]);

  // Load templates when document type (category) is selected
  useEffect(() => {
    const loadTemplates = async () => {
      // Reset templates when no document type is selected
      if (!documentType) {
        setTemplates([]);
        setTemplatesError(null);
        return;
      }

      // Find the selected category to get its category_type
      const selectedCategory = categories.find(cat => cat.id === documentType);
      if (!selectedCategory || !selectedCategory.category_type) {
        setTemplates([]);
        setTemplatesError(null);
        return;
      }

      // Map category.category_type to template.template_type
      const templateType = templateAPI.getTemplateTypeForCategory(selectedCategory.category_type);

      setLoadingTemplates(true);
      setTemplatesError(null);
      try {
        // Fetch templates filtered by template_type, enabled, and active status
        const fetchedTemplates = await templateAPI.getTemplates({
          template_type: templateType,
          is_enabled: true,
          status: 'active',
          include_usage_count: false // We don't need usage count for selection
        });
        setTemplates(fetchedTemplates);
      } catch (error) {
        setTemplatesError('Failed to load templates');
        console.error('Error loading templates:', error);
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, [documentType, categories, templateAPI]);

  // Filter templates (already filtered by API, but ensure is_enabled and status)
  const filteredTemplates = templates.filter(template => 
    template.is_enabled && template.status === 'active'
  );

  // Get selected template data
  const selectedTemplateData = templates.find(template => template.template_id === selectedTemplate);

  return (
    <div className="space-y-4">
      {/* Document Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <DocumentTextIcon className="w-4 h-4 inline mr-2" />
          Document Type
        </label>
        <select
          value={documentType || ''}
          onChange={(e) => onDocumentTypeChange(e.target.value || null)}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
          disabled={loadingCategories}
        >
          <option value="">
            {loadingCategories ? 'Loading categories...' : 'Select document type...'}
          </option>
          {!loadingCategories && categoriesError && (
            <option value="" disabled>
              {categoriesError}
            </option>
          )}
          {categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
        {categoriesError && (
          <div className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
            <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
            {categoriesError}
          </div>
        )}
      </div>

      {/* Templates */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <DocumentTextIcon className="w-4 h-4 inline mr-2" />
          Template <span className="text-gray-500 text-xs font-normal">(Optional)</span>
        </label>
        <select
          value={selectedTemplate || ''}
          onChange={(e) => onTemplateChange(e.target.value || null)}
          disabled={!documentType || loadingTemplates}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-lex-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">
            {loadingTemplates 
              ? 'Loading templates...' 
              : !documentType 
                ? 'Select document type first...' 
                : 'Select a template (optional)...'}
          </option>
          {filteredTemplates.map(template => (
            <option key={template.template_id} value={template.template_id}>
              {template.name}
            </option>
          ))}
        </select>
        {templatesError && (
          <div className="mt-2 flex items-center text-sm text-yellow-600 dark:text-yellow-400">
            <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
            {templatesError}
          </div>
        )}
        {selectedTemplateData && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {selectedTemplateData.description || 'No description available'}
          </p>
        )}
        {documentType && !loadingTemplates && filteredTemplates.length === 0 && !templatesError && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            No templates available for this document type. You can create templates in Settings → Document Management → Document Templates.
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Main DMS Right Panel Component
 */
export const DMSRightPanel: React.FC = () => {
  const navigate = useAnimatedNavigate();
    const {
    isPanelOpen,
    isPanelPinned,
    panelMode,
    panelWidth,
    currentDocument,
    isCreatingNewDocument,
    documentType,
    clientId,
    matterId,
    newDocumentState,
    initializeNewDocument,
    updateNewDocumentMetadata,
    saveNewDocument,

    closePanel,
    setPanelPinned,
    setPanelWidth,

  } = useDMSRightPanelStore();
  
  const documentAPI = useDocumentAPI();
  const userPageSetupService = new UserPageSetupDefaultsService();

  // Local state for document creation/editing
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentContent, setDocumentContent] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(clientId);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(matterId);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string | null>(documentType);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showMetadata, setShowMetadata] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isResizing, setIsResizingState] = useState(false);


  // Resize functionality - using refs to avoid stale closures
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  // Auto-save functionality
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Content change handler - now handled by EdgeDocs
  // const handleContentChange = useCallback((content: string) => {
  //   setDocumentContent(content);
  //   setHasUnsavedChanges(true);
  //   
  //   // Clear existing timeout
  //   if (autoSaveTimeoutRef.current) {
  //     clearTimeout(autoSaveTimeoutRef.current);
  //   }
  //   
  //   // Set new auto-save timeout
  //   autoSaveTimeoutRef.current = setTimeout(() => {
  //     handleAutoSave();
  //   }, 2000); // Auto-save after 2 seconds of inactivity
  // }, []);

  // Auto-save handler - now handled by EdgeDocs
  // const handleAutoSave = useCallback(async () => {
  //   if (!documentContent.trim() || !selectedClientId || !selectedMatterId) return;
  //   
  //   try {
  //     setIsSaving(true);
  //     
  //     // TODO: Implement actual auto-save API call
  //     console.log('Auto-saving document...', {
  //       title: documentTitle,
  //       content: documentContent,
  //       clientId: selectedClientId,
  //       matterId: selectedMatterId,
  //       type: selectedDocumentType,
  //       tags
  //     });
  //     
  //     // Simulate API call
  //     await new Promise(resolve => setTimeout(resolve, 500));
  //     
  //     setLastSaved(new Date());
  //     setHasUnsavedChanges(false);
  //   } catch (error) {
  //     console.error('Auto-save failed:', error);
  //   } finally {
  //     setIsSaving(false);
  //   }
  // }, [documentTitle, documentContent, selectedClientId, selectedMatterId, selectedDocumentType, selectedTemplate]);

  const handleSave = useCallback(async () => {
    if (!documentTitle.trim() || !documentContent.trim() || !selectedClientId || !selectedMatterId) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      setIsSaving(true);
      
      // TODO: Implement actual save API call
      console.log('Saving document...', {
        title: documentTitle,
        content: documentContent,
        clientId: selectedClientId,
        matterId: selectedMatterId,
        type: selectedDocumentType,
        templateId: selectedTemplate
      });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      // Show success message
      alert('Document saved successfully!');
      
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save document. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [documentTitle, documentContent, selectedClientId, selectedMatterId, selectedDocumentType, selectedTemplate]);

  const handleBuildDocument = useCallback(async () => {
    if (!documentTitle.trim() || !selectedClientId || !selectedMatterId) {
      alert('Please provide a document title, select a client, and select a matter');
      return;
    }
    
    try {
      setIsSaving(true);
      
      console.log('🏗️ [DMSRightPanel] Building new document...', {
        title: documentTitle,
        clientId: selectedClientId,
        matterId: selectedMatterId,
        type: selectedDocumentType,
        templateId: selectedTemplate
      });
      
      // Update the store with current form data
      updateNewDocumentMetadata({
        title: documentTitle,
        client_id: selectedClientId,
        matter_id: selectedMatterId,
        document_type: selectedDocumentType,
        template_id: selectedTemplate
      });
      
      // Create document using the store
      const creationResult: any = await saveNewDocument();
      const documentId: string = typeof creationResult === 'string'
        ? creationResult
        : (creationResult?.documentId || creationResult?.id);

      if (!documentId) {
        throw new Error('Document creation succeeded but no documentId was returned');
      }

      console.log('✅ [DMSRightPanel] Document created successfully with ID:', documentId);

      // Resolve versionId for immediate AST seeding
      // Also fetch document to populate cache with title (fixes race condition)
      let versionId: string | undefined = creationResult?.versionId || creationResult?.latestVersionId;
      let fullDoc: any = null;
      
      if (!versionId) {
        try {
          console.log('💾 [DMSRightPanel] Fetching document to get versionId and populate cache...');
          fullDoc = await documentAPI.getDocument(documentId);
          console.log('✅ [DMSRightPanel] Document fetched:', {
            documentId,
            title: fullDoc?.title,
            hasTitle: !!fullDoc?.title,
            latestVersion: fullDoc?.latest_version?.version_id
          });
          
          let resolved: string | undefined = fullDoc?.latest_version?.version_id;
          if (!resolved && Array.isArray((fullDoc as any)?.versions) && (fullDoc as any).versions.length > 0) {
            resolved = (fullDoc as any).versions[0]?.version_id;
          }
          versionId = resolved;
        } catch (e) {
          console.warn('⚠️ [DMSRightPanel] Failed to resolve versionId from API; will navigate without it', e);
        }
      } else {
        // Even if we have versionId, fetch document to populate cache with title
        try {
          console.log('💾 [DMSRightPanel] Fetching document to populate cache with title...');
          fullDoc = await documentAPI.getDocument(documentId);
          console.log('✅ [DMSRightPanel] Document fetched for cache:', {
            documentId,
            title: fullDoc?.title,
            hasTitle: !!fullDoc?.title
          });
        } catch (fetchErr) {
          console.warn('⚠️ [DMSRightPanel] Failed to fetch document for cache (non-fatal):', fetchErr);
        }
      }

      // --- MS Word path: skip AST seeding, navigate directly to Word editor ---
      const editorType = newDocumentState?.editor_type || 'edgedocs';
      if (editorType === 'msword') {
        console.log('📄 [DMSRightPanel] MS Word document — skipping AST seeding, navigating to word-editor');
        const urlParams = new URLSearchParams();
        if (versionId) urlParams.set('version', versionId);
        if (documentTitle) urlParams.set('title', documentTitle);
        const wordUrl = `/documents/${documentId}/word-editor${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
        closePanel();
        navigate(wordUrl);
        setIsSaving(false);
        return;
      }

      // Build minimal AST with metadata and reasonable defaults
      try {
        if (versionId) {
          // Priority 1: Fetch user page setup defaults with scope hierarchy
          let userDefaults = null;
          try {
            console.log('🎨 [DMSRightPanel] Fetching user page setup defaults...');
            userDefaults = await userPageSetupService.getUserDefaults({
              matterId: selectedMatterId || undefined,
              clientId: selectedClientId || undefined
            });
            console.log('✅ [DMSRightPanel] User defaults:', userDefaults);
          } catch (error) {
            console.warn('⚠️ [DMSRightPanel] Failed to fetch user defaults:', error);
          }

          // Priority 2: Fetch firm-wide system preferences
          let systemPreferences: SystemPreferences | null = null;
          try {
            console.log('🏢 [DMSRightPanel] Fetching firm-wide system preferences...');
            systemPreferences = await getSystemPreferences();
            console.log('✅ [DMSRightPanel] System preferences:', systemPreferences);
          } catch (error) {
            console.warn('⚠️ [DMSRightPanel] Failed to fetch system preferences (non-fatal):', error);
          }

          // Helper function to convert page size string to dimensions
          const getPaperSizeFromString = (pageSize: string | undefined): { name: string; width: number; height: number } => {
            if (!pageSize) {
              return { name: 'Letter (8.5" x 11")', width: 8.5, height: 11 };
            }

            const normalized = pageSize.toLowerCase().trim();
            
            // Map common page size strings to dimensions
            if (normalized.includes('letter') || normalized === 'letter') {
              return { name: 'Letter (8.5" x 11")', width: 8.5, height: 11 };
            } else if (normalized.includes('a4') || normalized === 'a4') {
              return { name: 'A4 (8.27" x 11.69")', width: 8.27, height: 11.69 };
            } else if (normalized.includes('legal') || normalized === 'legal') {
              return { name: 'Legal (8.5" x 14")', width: 8.5, height: 14 };
            } else if (normalized.includes('tabloid') || normalized === 'tabloid') {
              return { name: 'Tabloid (11" x 17")', width: 11, height: 17 };
            } else if (normalized.includes('a3') || normalized === 'a3') {
              return { name: 'A3 (11.69" x 16.54")', width: 11.69, height: 16.54 };
            }
            
            // Default to Letter if unrecognized
            return { name: 'Letter (8.5" x 11")', width: 8.5, height: 11 };
          };

          // Helper function to parse margin preset or use default
          const getMarginsFromPreset = (preset: string | undefined): { top: number; right: number; bottom: number; left: number } => {
            // For now, use default 1" margins
            // TODO: Implement margin preset parsing if presets are defined
            return { top: 1, right: 1, bottom: 1, left: 1 };
          };

          // Priority 3: CSS variables
          const css = getComputedStyle(document.documentElement);
          const cssFont = css.getPropertyValue('--font-document')?.trim() || '';
          const cssFontScale = parseFloat(css.getPropertyValue('--font-scale')?.trim() || '1.0');

          // Resolve font family: User → System → CSS → Hardcoded
          // ✅ PRIORITY 1 FIX: Normalize case and remove quotes for consistent storage
          const fontFamily = (userDefaults?.typography?.fontFamily 
            || systemPreferences?.document_font 
            || (() => {
                const raw = (cssFont || 'Times New Roman').replace(/^\s*"|"\s*$/g, '').trim();
                const firstComma = raw.split(',')[0];
                return firstComma.split(' - ')[0].trim();
              })())
            .replace(/'/g, '') // Remove single quotes
            .trim()
            // Normalize case: Capitalize first letter of each word (e.g., "inter" → "Inter", "times new roman" → "Times New Roman")
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');

          // Resolve font size: User → System (via scale) → CSS → Hardcoded
          const fontSizePx = userDefaults?.typography?.fontSize 
            || (systemPreferences?.font_size_scale ? (() => {
                const scale = parseFloat(systemPreferences.font_size_scale) || 1.0;
                return Math.max(8, Math.min(72, Math.round(14 * scale)));
              })() : undefined)
            || (() => {
                const fontScale = isFinite(cssFontScale) ? cssFontScale : 1.0;
                return Math.max(8, Math.min(72, Math.round(14 * fontScale)));
              })();

          // Resolve line height: User → System → Hardcoded
          // Helper to convert line spacing string to multiplier
          const convertLineSpacingToMultiplier = (spacing: string | undefined): number | undefined => {
            if (!spacing) return undefined;
            const normalized = spacing.toLowerCase().trim();
            switch (normalized) {
              case 'single': return 1.0;
              case '1.15': return 1.15;
              case '1.5': return 1.5;
              case 'double': return 2.0;
              case '2.5': return 2.5;
              case '3.0': case 'triple': return 3.0;
              default:
                // Try parsing as a number
                const parsed = parseFloat(spacing);
                return isFinite(parsed) ? parsed : undefined;
            }
          };
          const lineHeightMul = userDefaults?.typography?.lineHeight
            || convertLineSpacingToMultiplier(systemPreferences?.default_line_spacing)
            || 1.4;

          // Log line height resolution using DocumentLogger
          const lineHeightSource = userDefaults?.typography?.lineHeight ? 'userDefaults'
            : systemPreferences?.default_line_spacing ? 'systemPreferences'
            : 'hardcoded';
          DocumentLogger.defaults.lineHeight({
            resolvedValue: lineHeightMul,
            source: lineHeightSource,
            systemPrefsValue: systemPreferences?.default_line_spacing,
            userDefaultsValue: userDefaults?.typography?.lineHeight,
          });

          // Resolve page setup: User → System → Hardcoded
          const pageSetup = userDefaults ? {
            margins: userDefaults.margins || getMarginsFromPreset(systemPreferences?.default_margin_preset),
            paperSize: userDefaults.page?.pageSize 
              ? getPaperSizeFromString(userDefaults.page.pageSize)
              : getPaperSizeFromString(systemPreferences?.default_page_size),
            orientation: (userDefaults.page?.orientation || systemPreferences?.default_page_orientation || 'portrait') as 'portrait' | 'landscape',
            pageColor: '#ffffff'
          } : {
            // Use system preferences or fall back to hardcoded defaults
            margins: getMarginsFromPreset(systemPreferences?.default_margin_preset),
            paperSize: getPaperSizeFromString(systemPreferences?.default_page_size),
            orientation: (systemPreferences?.default_page_orientation || 'portrait') as 'portrait' | 'landscape',
            pageColor: '#ffffff'
          };

          // ✅ FIX: Create empty document - title is stored in metadata, not in document content
          // New documents should start empty with just a blinking caret at position 0
          // 🔥 FIX (2026-01-16): Generate unique UUIDs instead of hardcoded 'p1'/'r1'
          // Hardcoded IDs caused WASM_OVERLAPPING_LINES errors when paragraph boundaries changed
          const paraId = crypto.randomUUID();
          const runId = crypto.randomUUID();
          const ast = {
            blocks: [
              { type: 'Paragraph', id: paraId, children: [{ id: runId, text: '' }] }
            ],
            metadata: {
              title: documentTitle,
              client_id: selectedClientId,
              matter_id: selectedMatterId,
              pageSetup,
              fontSettings: { fontFamily, fontSizePx, lineHeightMul }
            }
          } as any;

          console.log('📝 [DMSRightPanel] AST being created (empty document):', {
            documentTitle,
            titleStoredInMetadata: true,
            contentIsEmpty: ast.blocks[0]?.children?.[0]?.text === '',
            astBlocks: ast.blocks,
            firstBlockText: ast.blocks[0]?.children?.[0]?.text
          });

          // Persist initial AST immediately
          const astUrl = `/api/v1/dms/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/ast`;
          console.log('📤 [DMSRightPanel] POSTing AST to:', astUrl);
          
          const astResponse = await fetch(astUrl, {
            method: 'POST',
            headers: {
              ...documentAPI.getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ ast, ast_version: 'v1' })
          });

          if (!astResponse.ok) {
            const errorText = await astResponse.text();
            console.error('❌ [DMSRightPanel] AST POST failed:', {
              status: astResponse.status,
              statusText: astResponse.statusText,
              error: errorText
            });
            throw new Error(`AST POST failed: ${astResponse.status} ${astResponse.statusText}`);
          }

          const astResult = await astResponse.json();
          console.log('✅ [DMSRightPanel] AST POST successful:', {
            documentId,
            versionId,
            astResult,
            savedAstBlocks: astResult?.ast?.blocks,
            savedFirstBlockText: astResult?.ast?.blocks?.[0]?.children?.[0]?.text
          });

          // Verify the AST was saved correctly by fetching it back
          // Wait for backend persistence before navigating
          console.log('🔍 [DMSRightPanel] Verifying AST was saved before navigation...');
          let astVerified = false;
          let verificationAttempts = 0;
          const maxAttempts = 5;
          
          while (!astVerified && verificationAttempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms between attempts
            
            try {
              const verifyResponse = await fetch(astUrl, {
                method: 'GET',
                headers: {
                  ...documentAPI.getAuthHeaders(),
                  'Content-Type': 'application/json'
                }
              });
              
              if (verifyResponse.ok) {
                const verifiedAst = await verifyResponse.json();
                const verifiedText = verifiedAst?.ast?.blocks?.[0]?.children?.[0]?.text;
                // ✅ FIX: New documents are created with empty text, verify empty string
                const matches = verifiedText === '';
                
                console.log(`✅ [DMSRightPanel] AST verification successful (attempt ${verificationAttempts + 1}):`, {
                  verifiedBlocks: verifiedAst?.ast?.blocks,
                  verifiedText,
                  matches,
                  expectedEmpty: true
                });
                
                if (matches) {
                  astVerified = true;
                } else {
                  console.warn(`⚠️ [DMSRightPanel] AST text mismatch. Expected empty string, Got: "${verifiedText}"`);
                }
              } else {
                console.log(`⏳ [DMSRightPanel] AST not ready yet (attempt ${verificationAttempts + 1}/${maxAttempts}):`, verifyResponse.status);
              }
            } catch (verifyErr) {
              console.log(`⏳ [DMSRightPanel] AST verification error (attempt ${verificationAttempts + 1}/${maxAttempts}):`, verifyErr);
            }
            
            verificationAttempts++;
          }
          
          if (!astVerified) {
            console.warn('⚠️ [DMSRightPanel] AST verification incomplete after max attempts, but proceeding with navigation');
          }
        } else {
          console.warn('⚠️ [DMSRightPanel] No versionId available; skipping initial AST seed');
        }
      } catch (seedErr) {
        console.error('❌ [DMSRightPanel] Seeding initial AST encountered an error:', seedErr);
        // Don't block navigation, but log the error
      }

      // ✅ FIX: Validate documentId before navigation
      if (!documentId || documentId.trim() === '') {
        console.error('❌ [DMSRightPanel] Cannot navigate: documentId is empty');
        alert('Error: Document ID is missing. Please try creating the document again.');
        return;
      }
      
      // Close the panel and navigate to the full document editor (include version and title when known)
      const urlParams = new URLSearchParams();
      if (versionId) {
        urlParams.set('version', versionId);
      }
      // Pass title via URL to avoid race condition with DocumentService cache
      if (documentTitle) {
        urlParams.set('title', documentTitle);
      }
      const url = `/documents/${documentId}/editor${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      
      console.log('🎯 [DMSRightPanel] Navigating to document editor page:', {
        documentId,
        versionId,
        documentTitle,
        url
      });
      closePanel();
      navigate(url);
      
    } catch (error) {
      console.error('❌ [DMSRightPanel] Failed to build document:', error);
      alert(`Failed to create document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [documentTitle, selectedClientId, selectedMatterId, selectedDocumentType, selectedTemplate, updateNewDocumentMetadata, saveNewDocument, closePanel, navigate]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        closePanel();
      }
    } else {
      closePanel();
    }
  }, [hasUnsavedChanges, closePanel]);

  const togglePin = useCallback(() => {
    setPanelPinned(!isPanelPinned);
  }, [isPanelPinned, setPanelPinned]);

  // Resize handlers - fixed coordinate calculation and improved usability
  const handleResize = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;
    
    // Fixed: Correct coordinate calculation for left/right dragging
    const deltaX = e.clientX - resizeStartXRef.current;
    const windowWidth = window.innerWidth;
    
    // Convert pixel delta to percentage change (negative because we're dragging left to expand)
    const deltaPercent = -(deltaX / windowWidth) * 100;
    const newWidthPercent = Math.max(25, Math.min(96, resizeStartWidthRef.current + deltaPercent));
    
    // Update panel width immediately for smooth drag experience
    setPanelWidth(newWidthPercent);
  }, [setPanelWidth]);

  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    setIsResizingState(false);
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleResize, setIsResizingState]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    setIsResizingState(true);
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = panelWidth;
    
    // Add visual feedback during resize
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [panelWidth, handleResize, handleResizeEnd, setIsResizingState]);

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResize, handleResizeEnd]);

  // Enhanced document initialization with content loading
  useEffect(() => {
    const loadDocumentContent = async () => {
      if (!currentDocument || (panelMode !== 'document_editor' && panelMode !== 'document_viewer')) return;
      
      try {
        setIsSaving(true); // Show loading state
        
        // Set basic document metadata immediately
        setDocumentTitle(currentDocument.title || '');
        setSelectedClientId(currentDocument.client_id || null);
        setSelectedMatterId(currentDocument.matter_id || null);
        setSelectedDocumentType(currentDocument.category_id || null);
        setSelectedTemplate(null); // Template will be set when document type is selected
        
        // Load document content from API
        const docId = currentDocument.document_id || currentDocument.id;
        if (docId) {
          try {
            // First try to get the full document details
            const fullDocument = await documentAPI.getDocument(docId);
            
            // Try to get extracted text content
            let content = '';
            if (fullDocument.extracted_text) {
              content = fullDocument.extracted_text;
            } else {
              // Try to get document content via lexical-content API
              try {
                const contentResponse = await documentAPI.getLexicalContent(docId);
                content = contentResponse.lexical_content.content || '';
              } catch (contentError) {
                console.log('No extracted text available for this document');
                content = `Document: ${currentDocument.title}\n\nContent will be available after processing.`;
              }
            }
            
            setDocumentContent(content);
            setHasUnsavedChanges(false);
          } catch (docError) {
            console.error('Error loading document content:', docError);
            setDocumentContent(`Document: ${currentDocument.title}\n\nError loading content: ${docError instanceof Error ? docError.message : 'Unknown error'}`);
          }
        }
        
      } catch (error) {
        console.error('Error initializing document:', error);
      } finally {
        setIsSaving(false);
      }
    };
    
    loadDocumentContent();
  }, [currentDocument, panelMode, documentAPI]);

  // Initialize new document when entering create mode
  useEffect(() => {
    if (panelMode === 'document_creator' && isCreatingNewDocument) {
      initializeNewDocument({
        client_id: clientId,
        matter_id: matterId,
        document_type: documentType
      });
    }
  }, [panelMode, isCreatingNewDocument, clientId, matterId, documentType, initializeNewDocument]);

  // Reset form when creating new document
  useEffect(() => {
    if (isCreatingNewDocument) {
      setDocumentTitle('');
      setDocumentContent('');
      setSelectedClientId(clientId);
      setSelectedMatterId(matterId);
      setSelectedDocumentType(documentType);
      setSelectedTemplate(null);
      setHasUnsavedChanges(false);

    }
  }, [isCreatingNewDocument, clientId, matterId, documentType]);

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Escape key handler to close panel and resize shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isPanelOpen) {
        handleClose();
      }
      
      // Panel resize shortcuts (Ctrl/Cmd + Arrow Keys)
      if (isPanelOpen && (event.ctrlKey || event.metaKey)) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          const newWidth = Math.max(25, panelWidth - 5);
          setPanelWidth(newWidth);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          const newWidth = Math.min(96, panelWidth + 5);
          setPanelWidth(newWidth);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPanelOpen, handleClose, panelWidth, setPanelWidth]);

  if (!isPanelOpen) return null;

  return (
    <div className={`h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col relative ${isResizing ? 'select-none' : ''}`}>
      {/* Resize Handle */}
      <div
        ref={resizeHandleRef}
        className={`absolute left-0 top-0 w-1 h-full cursor-ew-resize hover:bg-lex-accent hover:w-2 transition-all duration-200 bg-gray-300 dark:bg-gray-600 z-10 ${isResizing ? 'bg-lex-accent w-2 opacity-100' : 'opacity-60 hover:opacity-100'}`}
        onMouseDown={handleResizeStart}
        title="Drag to resize panel or use Ctrl/Cmd + Arrow Keys"
      />
      
      {/* Invisible resize area for better UX */}
      <div
        className={`absolute left-0 top-0 w-4 h-full cursor-ew-resize z-20 hover:bg-lex-accent/10 transition-colors duration-200 ${isResizing ? 'bg-lex-accent/20' : ''}`}
        onMouseDown={handleResizeStart}
        title="Drag to resize panel or use Ctrl/Cmd + Arrow Keys"
      />
      
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-lex-accent-soft">
            {panelMode === 'document_creator' && <PlusIcon className="w-5 h-5 text-lex-accent" />}
            {panelMode === 'document_editor' && <PencilIcon className="w-5 h-5 text-lex-accent" />}
            {panelMode === 'document_viewer' && <EyeIcon className="w-5 h-5 text-lex-accent" />}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {panelMode === 'document_creator' && 'Create New Document'}
              {panelMode === 'document_editor' && 'Edit Document'}
              {panelMode === 'document_viewer' && 'View Document'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isSaving && panelMode === 'document_editor' && currentDocument && 'Loading document content...'}
              {isSaving && panelMode === 'document_viewer' && currentDocument && 'Loading document...'}
              {isSaving && panelMode === 'document_creator' && 'Building document...'}
              {!isSaving && panelMode === 'document_creator' && 'Fill in details and click Build Document'}
              {!isSaving && lastSaved && panelMode !== 'document_creator' && `Last saved: ${lastSaved.toLocaleTimeString()}`}
              {!isSaving && !lastSaved && hasUnsavedChanges && panelMode !== 'document_creator' && 'Unsaved changes'}
              {!isSaving && !lastSaved && !hasUnsavedChanges && currentDocument && panelMode !== 'document_creator' && 'Document loaded'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {/* Auto-save indicator */}
          {isSaving && (
            <div className="flex items-center space-x-2 text-sm text-lex-accent">
              <div className="w-4 h-4 border-2 border-lex-accent border-t-transparent rounded-full animate-spin"></div>
              <span>
                {panelMode === 'document_editor' && currentDocument ? 'Loading...' : 
                 panelMode === 'document_viewer' && currentDocument ? 'Loading...' : 
                 'Saving...'}
              </span>
            </div>
          )}
          
          {/* Pin/Unpin button */}
          <button
            onClick={togglePin}
            className={`p-2 rounded-lg transition-colors ${
              isPanelPinned 
                ? 'bg-lex-accent-soft text-lex-accent'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title={isPanelPinned ? 'Unpin panel' : 'Pin panel'}
          >
            <PaperClipIcon className="w-5 h-5" />
          </button>
          
          {/* Close button */}
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Close panel (Esc)"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {panelMode === 'document_creator' ? (
          /* New Document Creation with Inline Editor */
          <>
            {/* Document Creation Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                placeholder="Enter document title..."
                className="w-full text-xl font-semibold bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            {/* Editor Type Selector */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Editor
              </label>
              <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button
                  type="button"
                  onClick={() => updateNewDocumentMetadata({ editor_type: 'edgedocs' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                    (newDocumentState.editor_type || 'edgedocs') === 'edgedocs'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  <DocumentTextIcon className="w-4 h-4" />
                  EdgeDocs
                </button>
                <button
                  type="button"
                  onClick={() => updateNewDocumentMetadata({ editor_type: 'msword' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                    newDocumentState.editor_type === 'msword'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  <span className="font-bold text-xs leading-none">W</span>
                  Microsoft Word
                </button>
              </div>
              {newDocumentState.editor_type === 'msword' && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Creates a .docx file for editing in Microsoft Word
                </p>
              )}
            </div>

            {/* Metadata Section for New Documents */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowMetadata(!showMetadata)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">Document Details</span>
                {showMetadata ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>
              
              {showMetadata && (
                <div className="p-4 pt-0 space-y-6">
                  <ClientMatterSelector
                    selectedClientId={selectedClientId}
                    selectedMatterId={selectedMatterId}
                    onClientChange={setSelectedClientId}
                    onMatterChange={setSelectedMatterId}
                  />
                  
                  <DocumentClassification
                    documentType={selectedDocumentType}
                    onDocumentTypeChange={setSelectedDocumentType}
                    selectedTemplate={selectedTemplate}
                    onTemplateChange={setSelectedTemplate}
                  />
                </div>
              )}
            </div>

            {/* Instructions for Document Creation */}
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ready to Create Document
                </h4>
                <p className="text-gray-500 dark:text-gray-400 max-w-sm">
                  Fill in the document details above, then click "Build Document" to create and open the document editor.
                </p>
              </div>
            </div>
          </>
        ) : (
          /* Existing Document Editor/Viewer */
          <>
            {/* Document Title */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                placeholder="Document Title"
                className="w-full text-xl font-semibold bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                disabled={panelMode === 'document_viewer'}
              />
            </div>

            {/* Metadata Section */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowMetadata(!showMetadata)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">Document Details</span>
                {showMetadata ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>
              
              {showMetadata && (
                <div className="p-4 pt-0 space-y-6">
                  <ClientMatterSelector
                    selectedClientId={selectedClientId}
                    selectedMatterId={selectedMatterId}
                    onClientChange={setSelectedClientId}
                    onMatterChange={setSelectedMatterId}
                  />
                  
                  <DocumentClassification
                    documentType={selectedDocumentType}
                    onDocumentTypeChange={setSelectedDocumentType}
                    selectedTemplate={selectedTemplate}
                    onTemplateChange={setSelectedTemplate}
                  />
                </div>
              )}
            </div>

            {/* Document Actions */}
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {panelMode === 'document_viewer' ? 'View Document' : 'Edit Document'}
                </h4>
                <p className="text-gray-500 dark:text-gray-400 max-w-sm mb-6">
                  {panelMode === 'document_viewer' 
                    ? 'Click below to open the document viewer.'
                    : 'Click below to open the full document editor.'
                  }
                </p>
                <button
                  onClick={() => {
                    const docId = currentDocument?.id || currentDocument?.document_id;
                    if (docId) {
                      closePanel();
                      navigate(`/documents/${docId}/editor`);
                    }
                  }}
                  className="px-6 py-3 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
                >
                  {panelMode === 'document_viewer' ? 'Open Viewer' : 'Open Editor'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer with actions */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            {hasUnsavedChanges && (
              <span className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Unsaved changes</span>
              </span>
            )}
            {lastSaved && !hasUnsavedChanges && (
              <span className="flex items-center space-x-1">
                <CheckCircleIcon className="w-4 h-4 text-green-500" />
                <span>Saved</span>
              </span>
            )}
            {panelMode === 'document_creator' && (
              <span className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-lex-accent rounded-full"></div>
                <span>Draft v1.0</span>
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={panelMode === 'document_creator' ? handleBuildDocument : handleSave}
              disabled={isSaving || !documentTitle.trim() || !selectedClientId || !selectedMatterId}
              className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Saving...' : (panelMode === 'document_creator' ? 'Build Document' : 'Save Document')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 